import React, { useContext, useEffect, useState } from 'react';

import {
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';

import {
  useHistory,
  withRouter,
} from 'react-router-dom';

import PropTypes from 'prop-types';

import AlertDialog from '../../modals/alertDialog';
import BasicButton from '../../buttons/basicButton';
import BreadCrumb from '../../fields/breadCrumb';
import PatientsList from './patientsList';
import PatientsListBasic from './patientsListBasic';
import CustomSnackbar from '../../feedback/customSnackbar';
import Footer from '../../fields/footer';
import Header from '../../fields/header';
import MenuDrawer from '../../fields/menuDrawer';
import SpinnerLoading from '../../feedback/spinnerLoading';

import { deletePatient, getEnrolments, getPatients, getProgramPatients, getPrograms } from '../../../utils/comms';

import { extractAdministeredProgramsId } from '../../../utils/helpers';

import translations from '../../../languages/i18n';

import generalStyles from '../../../styles/generalStyles';
import { sectionTitle } from '../../../styles/formElementsStyles';

// NOTA: Per poder emprar el react-router-dom i enllaçar pàgines s'ha de convertir el component en una funció i exportar-lo embolicat per withRouter
// En cas de que el component sigui un fill directe de Route no fa falta fer-ho
// Explicat al punt 2.1. Using withRouter de https://dev.to/projectescape/programmatic-navigation-in-react-3p1l

import { SessionContext } from '../../../utils/context';

function ListPatients({ lang, handleChangeLang }) {
  const sessionContext = useContext(SessionContext);

  const [administeredProgramsList, setAdministeredProgramsList] = useState([]);
  const [administeredProgramsListInfo, setAdministeredProgramsListInfo] = useState([]);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [obtainPatients, setObtainPatients] = useState(false);
  const [patientsList, setPatientsList] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showProgramsList, setShowProgramsList] = useState(false);
  const [showPatientsList, setShowPatientsList] = useState(false);
  const [typeOfClinician, setTypeOfClinician] = useState('');

  const history = useHistory();

  const classes = generalStyles();
  const sectionTitleStyle = sectionTitle();

  const manageFeedback = (fbType, fbMessage) => {
    setFeedbackMessage(translations[`_${fbType}${fbMessage}`]);
    setFeedbackType(fbType);
    setShowFeedback(true);
  };

  useEffect(() => {
    const getInfoFromServer = async () => {
      setObtainPatients(false);
      setShowLoading(true);
      let errorDuringTheProcess = false;
      let typeOfError = 'error'; // Valor per defecte

      if (sessionContext.role === 'Clinician') {
        let clinicianType = '';
        // Miram si el clínic és administrador d'algun programa
        const programs = await getPrograms(sessionContext.userToken); // Obtenim el llistat dels programes als quals té accés el clínic
        if (programs.status === 200) { // Tenim els programes            
          // Agafam els ids dels programes administrats pel Clínic
          const administeredPrograms = extractAdministeredProgramsId(programs.data.programs, sessionContext.userId);
          setAdministeredProgramsList(administeredPrograms);
          // Si hi ha al manco un programa a l'array vol dir que l'usuari clínic és administrador
          const programAdmin = administeredPrograms.length !== 0;
          // Si no es administrador de cap programa, el clínic només ha de veure el llistat de pacients dels que forma part de l'equip clínic de la subscripció (està dins supervisedBy d'Enrolment)
          if (!programAdmin) {
            const patientsFromServer = await getPatients(sessionContext.userToken);
            if (patientsFromServer.status === 200) {
              const { patients } = patientsFromServer.data;
              // Per a un clínic, getEnrolments només torna els enrolments dels que en forma part (el clínic està dins el supervisedBy), són els pacients sobre els que el clínic té "autoritat"
              const enrolments = await getEnrolments(sessionContext.userToken);
              if (enrolments.status === 200) { // Tenim enrolments
                // Comprovam si el clínic supervisa algún enrolment
                // flatMap torna un array d'una dimensió, ja que el map de supervisedBy retorna arrays de diferent longitud
                const supervisionsArray = enrolments.data.enrolments.flatMap((enrolment) => {
                  return enrolment.supervisedBy.map((member) => member.clinician === sessionContext.userId && member.admin === true);
                });
                const isSupervisor = supervisionsArray.find((element) => element === true) || false; // Si el find dona undefined, isSupervisor serà false
                clinicianType = isSupervisor ? 'Supervisor' : 'Basic';
                // Ficam dins un array l'id del pacient de cada enrolment
                const patientsWithEnrolments = enrolments.data.enrolments.map((enrolment) => {
                  return enrolment.patient._id;
                });
                // TODO analitzar i, si és possible, ajuntar les dues operacions en una per només recòrrer una vegada l'array d'enrolments
                // Eliminam els ids duplicats
                const patientsIds = [...new Set(patientsWithEnrolments)];
                // Filtram el llistat de pacients rebuts del servidor per només quedar-mos amb els pacients amb el que es comparteix enrolment
                const filteredPatients = patients.filter((patient) => patientsIds.some((patientId) => patientId === patient._id));
                setPatientsList(filteredPatients);
                setShowPatientsList(true);
              } else { // Error amb les dades
                errorDuringTheProcess = true;
                if (enrolments === 'timeOut') typeOfError = 'timeOut';
              }
            } else { // Error amb les dades
              errorDuringTheProcess = true;
              if (patientsFromServer === 'timeOut') typeOfError = 'timeOut';
            }            
          } else if (administeredPrograms.length === 1) { // Si el clínic és administrador de només un programa, veurà el llistat de tots els pacients del programa            
            const patientsFromServer = await getPatients(sessionContext.userToken);
            if (patientsFromServer.status === 200) {
              const { patients } = patientsFromServer.data;
              setPatientsList(patients);
              clinicianType = 'Administrator';
              setShowPatientsList(true);
            } else {
              // Error amb les dades
              errorDuringTheProcess = true;
              if (patientsFromServer === 'timeOut') typeOfError = 'timeOut';
            }
          } else {
            const administeredProgramsDetailedInfo = programs.data.programs.filter((program) => {
              // Agafam els programes on el clínic és administrador
              return program.accessibleBy.clinicians.find((clinician) => clinician.clinician !== null && clinician.clinician._id === sessionContext.userId && clinician.admin === true);
            });
            // Ens quedam amb el id i el shareId de cada programa            
            // S'afegeix name amb la traducció del programa en l'idioma que s'ha carregat la pàgina i s'ordena alfabèticament pel nom del programa. sensitivity: 'base' ignora majúscules/minúscules i accents
            const administeredProgramsInfo = administeredProgramsDetailedInfo.map((program) => ({programId: program._id, sharedId: program.sharedId, name: translations[program.sharedId]})).sort((a, b) => (a.name || '').localeCompare(b.name || '', { sensitivity: 'base' }));
            setAdministeredProgramsListInfo(administeredProgramsInfo);
            setShowProgramsList(true);
          }
        } else { // Error amb les dades
          errorDuringTheProcess = true;
          if (programs === 'timeOut') typeOfError = 'timeOut';
        }
        setTypeOfClinician(clinicianType);
      } else { // Si l'usuari no és clinic (és administrador del sistema), veurà el llistat de tots els pacients
        const patientsFromServer = await getPatients(sessionContext.userToken);
        if (patientsFromServer.status === 200) {
          const { patients } = patientsFromServer.data;            
          setShowPatientsList(true);
          setPatientsList(patients);          
        } else {
          errorDuringTheProcess = true;
          if (patientsFromServer === 'timeOut') typeOfError = 'timeOut';
        }
      }

      setShowLoading(false);
      if (errorDuringTheProcess) {
        if (typeOfError === 'timeOut') { // Si hi ha hagut un error durant el procés, miram si ha estat de tipus timeOut
          manageFeedback('error', 'TimeOut');
        } else { // Error amb les dades o al servidor
          manageFeedback('error', 'Generic');
        }
      }
    };
    getInfoFromServer();
  }, [obtainPatients]);

  const deletePatients = async () => {
    setShowLoading(true);
    // Esborram usuaris
    const responseDelete = await Promise.all(selectedPatients.map((element) => deletePatient(element.id, sessionContext.userToken)));
    // Comprovam si dins les respostes n'hi ha alguna de timeOut o erronia
    const allOk = responseDelete.map((answer) => {
      if (answer.status && answer.status === 200) return 'ok';
      if (answer === 'timeOut') return 'timeOut';
      return 'error';
    });
    const idTO = allOk.indexOf('timeOut');
    const idE = allOk.indexOf('error');
    if (idTO > -1 || idE > -1) { // Al manco hi ha 1 timeOut o 1 error
      // Miram si algun dels errors rebuts és un timeOut
      if (idTO > -1) { // Hi ha hagut un timeOut
        manageFeedback('error', 'TimeOut');
      } else { // Hi ha hagut un error
        manageFeedback('error', 'Generic');
      }
    } else { // No hi ha cap error
      manageFeedback('success', 'DeletePatient');
    }
    setSelectedPatients([]);
    // Indicam al useEffect que es torni a executar
    setObtainPatients(true);
  };

  const viewPatient = (id) => {
    history.push(`/${sessionContext.role.toLowerCase()}/patientInfo/${id}`);
  };

  const viewPatientSchedules = (id) => {
    history.push(`/${sessionContext.role.toLowerCase()}/patientSchedulesInfo/${id}`);
  };

  const newPatient = () => {
    history.push(`/${sessionContext.role.toLowerCase()}/newPatientInfo`);
  };

  const selectedPatient = (id, username) => {
    const selectedPatientsCopy = selectedPatients.slice();
    // Cercam si dins selectedPatientsCopy hi ha el valor de clinician
    const idx = selectedPatientsCopy.findIndex((item) => item.id === id);
    if (idx < 0) selectedPatientsCopy.push({ id, username }); // Si idx és -1 vol dir que no ha trobat el valor, l'afegim a la taula
    else selectedPatientsCopy.splice(idx, 1); // L'ha trobat l'eliminam de la taula
    setSelectedPatients(selectedPatientsCopy);
  };

  const gotoScreen = (screen) => {
    history.push(`/${sessionContext.role.toLowerCase()}/${screen}`);
  };

  const showMenuDrawer = (show) => {
    setShowDrawer(show);
  };

  const manageDeleteAlertAnswer = (agree) => {
    setShowDeleteAlert(false);
    if (agree) deletePatients();
  };

  // Demana els pacients del programa
  const getProgramPatientsFromServer = async (event) => {
    setShowLoading(true);
    setShowPatientsList(false);
    const programPatientsFromServer = await getProgramPatients(event.target.value, sessionContext.userToken);
    if (programPatientsFromServer.status === 200) {
      const { patients } = programPatientsFromServer.data;      
      setPatientsList(patients);
      setShowPatientsList(true);
      setTypeOfClinician('Administrator');
    } else {      
      if (programPatientsFromServer === 'timeOut'){ // Si hi ha hagut un error durant el procés, miram si ha estat de tipus timeOut
        manageFeedback('error', 'TimeOut');
      } else { // Error amb les dades o al servidor
        manageFeedback('error', 'Generic');
      }
    }
    setShowLoading(false);
  };

  return (
    <div className={classes.content}>
      <SpinnerLoading show={showLoading} />
      {/* ATENCIÓ: el role ve del servidor amb la primera lletra en majuscula, jo ho necessit tot en minuscula */}
      <Header type={sessionContext.role.toLowerCase()} handleShowDrawer={(show) => showMenuDrawer(show)} lang={lang} handleChangeLang={handleChangeLang} />
      <MenuDrawer
        type={sessionContext.role.toLowerCase()}
        showDrawer={showDrawer}
        handleShowDrawer={(show) => showMenuDrawer(show)}
        handleGotoScreen={(screen) => gotoScreen(screen)}
      />
      <AlertDialog
        open={showDeleteAlert}
        handleClose={(agree) => manageDeleteAlertAnswer(agree)}
        title={translations['_deletePatientsAlertTitle']}
        message={translations['_deletePatientsAlertMessage']}
        data={selectedPatients}
        field='username'
      />
      <CustomSnackbar
        show={showFeedback}
        handleCloseFeedback={(show) => setShowFeedback(show)}
        type={feedbackType}
        message={(showFeedback && feedbackMessage) || ''}
      />
      <div className={classes.body}>
        <Grid container direction='column' spacing={2}>
          {/* ----------------------- Fila 0 ----------------------- */}
          <Grid item xs={12}>
            <BreadCrumb role={sessionContext.role.toLowerCase()} home='listPatients' lang={lang} />
          </Grid>
          {/* ----------------------- Fila 1 ----------------------- */}
          <Grid item className={sectionTitleStyle.root}>
            <Grid container justify='space-between' spacing={2}>
              <Grid item>
                <Typography variant='h4' component='div' className={sectionTitleStyle.title}>
                  {sessionContext.role === 'Admin' ? translations['_allPatients'] : translations['_patients']}
                </Typography>
              </Grid>
              <Grid item>
                {sessionContext.role === 'Admin'
                && <BasicButton handleClick={() => newPatient()} type='accept' text={translations['_newPatient']} />
                }
              </Grid>
            </Grid>
          </Grid>
          {/* ----------------------- Fila 2 ----------------------- */}
          {showProgramsList && <Grid item className={sectionTitleStyle.root}>
            <Grid container justify='flex-start' spacing={2}>
              <Grid item>
                <Typography variant='h6' component='div'>{translations['_selectProgram']}</Typography>
              </Grid>
              <Grid item>
                <FormControl style={{ minWidth: 200 }}>
                  <Select label='program' onChange={getProgramPatientsFromServer}>
                    {administeredProgramsListInfo.map((programInfo) => <MenuItem  key={`prog_${programInfo.programId}`} value={programInfo.programId}>{translations[programInfo.sharedId]}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>}
          {/* ----------------------- Fila 3 ----------------------- */}
          {showPatientsList && (<Grid item>
            <Grid container justify='space-between' spacing={2}>
              <Grid item xs={12}>
                {(patientsList.length === 0 && sessionContext.role !== 'Admin') ? (
                  <Typography variant='h6' component='div' className={sectionTitleStyle.title}>
                    {translations['_emptyPatients']}
                  </Typography>
                ) : (
                  ((typeOfClinician === 'Administrator' || typeOfClinician === 'Supervisor') && (
                    <PatientsList
                      administeredPrograms={administeredProgramsList}
                      patientsData={patientsList}
                      selectedPatients={selectedPatients}
                      handleViewPatient={(patient) => viewPatient(patient)}
                      handleViewPatientSchedules={ (patient) => viewPatientSchedules(patient)}
                      handleDelete={() => setShowDeleteAlert(true)}
                      handleSelectedPatientsData={(id, username) => selectedPatient(id, username)}
                    />
                  )) || (
                    <PatientsListBasic
                      administeredPrograms={administeredProgramsList}
                      patientsData={patientsList}
                      selectedPatients={selectedPatients}
                      handleViewPatient={(patient) => viewPatient(patient)}
                      handleViewPatientSchedules={ (patient) => viewPatientSchedules(patient)}
                      handleDelete={() => setShowDeleteAlert(true)}
                      handleSelectedPatientsData={(id, username) => selectedPatient(id, username)}
                    />
                  )
                )}
              </Grid>
            </Grid>
          </Grid>)}
        </Grid>
      </div>
      <Footer />
    </div>
  );
}

export default withRouter(ListPatients);

// Indica i controla els props que se han de passar al component
ListPatients.propTypes = {
  handleChangeLang: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
};
